import { useRef, useEffect, useImperativeHandle, forwardRef } from "react";

/**
 * Canvas de firma manuscrita para el panel web.
 *
 * API pública a través de ref:
 *   - getDataUrl()  → string PNG en base64, o null si el canvas está vacío
 *   - clear()       → borra el trazo y resetea el estado
 *
 * No tiene dependencias externas: usa la Canvas API nativa del navegador.
 * Soporta ratón y touch. Ajusta la resolución para pantallas HiDPI/Retina.
 */

export interface SignaturePadRef {
  getDataUrl(): string | null;
  clear(): void;
}

interface Props {
  strokeColor?: string;
  backgroundColor?: string;
}

export const SignaturePad = forwardRef<SignaturePadRef, Props>(
  function SignaturePad(
    { strokeColor = "#111827", backgroundColor = "#ffffff" },
    ref,
  ) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawing = useRef(false);
    const hasStroke = useRef(false);
    const latestDataUrl = useRef<string | null>(null);

    useImperativeHandle(ref, () => ({
      getDataUrl() {
        return latestDataUrl.current;
      },
      clear() {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, rect.width, rect.height);
        hasStroke.current = false;
        latestDataUrl.current = null;
      },
    }));

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const setup = () => {
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 2.5;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, rect.width, rect.height);
      };

      setup();

      const getPos = (e: MouseEvent | TouchEvent) => {
        const rect = canvas.getBoundingClientRect();
        const src: { clientX: number; clientY: number } =
          "touches" in e && e.touches.length > 0
            ? (e.touches[0] as Touch)
            : (e as MouseEvent);
        return { x: src.clientX - rect.left, y: src.clientY - rect.top };
      };

      const onStart = (e: MouseEvent | TouchEvent) => {
        e.preventDefault();
        drawing.current = true;
        hasStroke.current = true;
        const p = getPos(e);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
      };

      const onMove = (e: MouseEvent | TouchEvent) => {
        if (!drawing.current) return;
        e.preventDefault();
        const p = getPos(e);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      };

      const onEnd = (e: MouseEvent | TouchEvent) => {
        if (!drawing.current) return;
        e.preventDefault();
        drawing.current = false;
        ctx.closePath();
        if (hasStroke.current) {
          latestDataUrl.current = canvas.toDataURL("image/png");
        }
      };

      canvas.addEventListener("mousedown", onStart);
      canvas.addEventListener("mousemove", onMove);
      canvas.addEventListener("mouseup", onEnd);
      canvas.addEventListener("mouseleave", onEnd);
      canvas.addEventListener("touchstart", onStart, { passive: false });
      canvas.addEventListener("touchmove", onMove, { passive: false });
      canvas.addEventListener("touchend", onEnd, { passive: false });

      const ro = new ResizeObserver(() => {
        const saved = hasStroke.current ? canvas.toDataURL("image/png") : null;
        setup();
        if (saved) {
          const img = new Image();
          img.onload = () => {
            const rect = canvas.getBoundingClientRect();
            ctx.drawImage(img, 0, 0, rect.width, rect.height);
          };
          img.src = saved;
        }
      });
      ro.observe(canvas);

      return () => {
        canvas.removeEventListener("mousedown", onStart);
        canvas.removeEventListener("mousemove", onMove);
        canvas.removeEventListener("mouseup", onEnd);
        canvas.removeEventListener("mouseleave", onEnd);
        canvas.removeEventListener("touchstart", onStart);
        canvas.removeEventListener("touchmove", onMove);
        canvas.removeEventListener("touchend", onEnd);
        ro.disconnect();
      };
    }, [strokeColor, backgroundColor]);

    return (
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          cursor: "crosshair",
          touchAction: "none",
        }}
        aria-label="Área de firma — dibuja tu firma con el ratón o el dedo"
      />
    );
  },
);
